/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useMemo, MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFlow1HiringAssistant, useHiringAssistant, useHiringAssistantContinue, useTaskReady, useUploadJobDescription, useTechnicalCompetencePost, TechnicalCompetencePayload, useCvAnalysisStatus } from 'hooks';
import { useCredits } from 'hooks/useCredits';
import { ConversationOption } from 'features';
import { Message, parseJobAssessmentData } from 'components';
import { parseCriteriaData } from 'helpers/parseCriteriaData';
import { handleStateTransition } from 'helpers/handleStateTransition';
import { isTalentAudit, TALENT_AUDIT_OPTION } from 'helpers/assistantFlow';
import {
  ADD_CUSTOM_QUESTIONS_OPTION,
  clearCustomQuestionDraft,
  componentTypeForCustomQuestion,
  CustomQuestionOption,
  CUSTOM_QUESTION_TYPE_OPTIONS,
  customQuestionTypeForOption,
  isCustomQuestionOption,
  isCustomQuestionStage,
  openCustomQuestionDraft,
  withCustomQuestionEntry,
} from 'helpers/customQuestionDraft';
import { appendMessages } from 'helpers/messageDedupe';
import { getWelcomeOptionLabel } from 'components/chat/recruitment-cards/welcome-options';
import { LoadingPhase } from 'components/chat/loading-indicator';
import { getAssistantComposerConfig, InputHints } from 'helpers/assistantInputHints';
import { getComposerDocumentHint } from 'helpers/documentMessage';

/**
 * Reads a file as base64 with the data-URL prefix stripped — the shape the
 * hiring-assistant endpoint expects for JSON `file` / `files` payloads.
 */
const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });

export const appendAssistantIdToUrlAsQuery = (id: number | string) => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('id2', String(id));
  window.history.pushState({}, '', currentUrl);
};


interface UseResponseProcessingProps {
  assessmentId: string | null;
  setAssessmentId: (id: string | null) => void;
  assistantId: string | null;
  setAssistantId: (id: string | null) => void;
  conversationState: string;
  setConversationState: (state: string) => void;
  setMessages: (value: Message[] | ((prev: Message[]) => Message[])) => void;
  messages: Message[];
  currentOptions: ConversationOption[] | null;
  setCurrentOptions: (options: ConversationOption[] | null) => void;
  setShowOption: (show: boolean) => void;
  setShowInput: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;
  setAssessmentStarted: (started: boolean) => void;
  assessmentStarted: boolean;
  assessmentStatus: 'idle' | 'initial' | 'ready';
  setAssessmentStatus: (assessmentStatus: 'idle' | 'initial' | 'ready') => void;
  currentTechnicalQuestionsRef?: MutableRefObject<TechnicalCompetencePayload | null>;
  setInputHints?: (hints: InputHints | null) => void;
}

export const useResponseProcessing = ({
  assessmentId,
  setAssessmentId,
  assistantId,
  setAssistantId,
  conversationState,
  setConversationState,
  setMessages,
  messages,
  currentOptions,
  setCurrentOptions,
  setShowOption,
  setShowInput,
  setIsLoading,
  setAssessmentStarted,
  assessmentStarted,
  assessmentStatus,
  setAssessmentStatus,
  currentTechnicalQuestionsRef,
  setInputHints,
}: UseResponseProcessingProps) => {
  const [isSubmittedQuestions, setIsSubmittedQustions] = useState(false);
  const [imageUploadScroll, setImageUploadScroll] = useState(false);
  // The options the recruiter left behind when opening the custom-questions
  // stage. flow_2 always comes back to the same Proceed CTA, but the audit has to
  // come back to whatever its assistant offered, so they are held here until the
  // type choice persists them with the draft.
  const customQuestionResumeOptionsRef = useRef<CustomQuestionOption[] | null>(null);
  const [showShortOnCreditModal, setShowShortOnCreditModal] = useState(false);
  const [showHiringProgressModal, setShowHiringProgressModal] = useState(false);
  const [creditErrorInfo, setCreditErrorInfo] = useState<{ creditsRequired: number; creditsAvailable: number } | null>(null);
  const { balance: creditBalance, canAfford } = useCredits();

  /**
   * Pre-flight credit check for an action about to be sent to the server.
   * Returns false (and surfaces the right prompt) when the balance is short —
   * a zero balance gets the celebratory progress modal, a partial one the
   * short-on-credit warning.
   */
  const ensureCredits = (creditsNeeded: number): boolean => {
    if (canAfford(creditsNeeded)) return true;
    setCreditErrorInfo({ creditsRequired: creditsNeeded, creditsAvailable: creditBalance });
    if (creditBalance === 0) {
      setShowHiringProgressModal(true);
    } else {
      setShowShortOnCreditModal(true);
    }
    return false;
  };
  /**
   * Is there a custom-question form on screen that the recruiter still has to
   * submit or skip? Only that should stop another form being opened — a form
   * that's already been answered, or one replayed from history, must not.
   */
  const hasOpenCustomQuestionForm = (): boolean =>
    !isSubmittedQuestions &&
    messages.some(
      (message) =>
        message.componentType === 'addSingleQuestion' ||
        message.componentType === 'addMultipleQuestions'
    );

  const { response, loading, error, sendRequest } = useHiringAssistant(assessmentId);
  const { response: flow1Response, loading: flow1Loading, error: flow1Error, sendRequest: flow1SendRequest } = useFlow1HiringAssistant(assistantId);
  const [lastFlow1Signature, setLastFlow1Signature] = useState<string | null>(null);
  const [completedTaskIds, setCompletedTaskIds] = useState<Set<string>>(new Set());
  const { submitTechnicalCompetence, loading: technicalCompetenceLoading } = useTechnicalCompetencePost();
  const { response: continueResponse, sendRequest: continueSendRequest } = useHiringAssistantContinue(assessmentId);
  const { uploadResponse, isLoading: uploadLoading, error: uploadError, uploadJobDescription } = useUploadJobDescription(assessmentId);
  const { taskResponse, error: taskError, startPolling, isPolling, loading: loadingTaskReady } = useTaskReady();
  const { isPolling: isCvAnalysisPolling, status: cvAnalysisStatus, startPolling: startCvAnalysisPolling, stopPolling: stopCvAnalysisPolling } = useCvAnalysisStatus();
  const [cvJustUploaded, setCvJustUploaded] = useState(false);
  const [pendingFlow1Data, setPendingFlow1Data] = useState<any>(null);
  const [lastProcessedResponse, setLastProcessedResponse] = useState<any>(null);
  const [lastProcessedContinueResponse, setLastProcessedContinueResponse] = useState<any>(null);
  const [lastAction, setLastAction] = useState<{ type: string; payload: any } | null>(null);
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>('general');
  const [candidatesToInvite, setCandidatesToInvite] = useState<{full_name: string; email: string}[]>([]);
  const navigate = useNavigate();

  const getPhaseFromState = (state: string): LoadingPhase => {
    switch (state) {
      case 'upload_jd':
      case 'get_jd_upload':
        return 'jd_upload';
      case 'upload_cvs':
      case 'upload_cv':
        return 'cv_upload';
      case 'get_experience_level':
        return 'experience_level';
      case 'generate_basic_requirements':
      case 'basic_requirements':
        return 'basic_questions';
      case 'generate_jd':
      case 'generating_jd':
        return 'generating_jd';
      case 'generate_criteria':
      case 'basic_expectation':
        return 'generating_criteria';
      case 'technical_competence':
      case 'generate_technical_questions':
        return 'technical_questions';
      default:
        return 'general';
    }
  };

  const handleRetry = () => {
    if (!lastAction) return;

    setLoadingPhase('retry');
    setIsLoading(true);
    // Remove the error message from the chat
    setMessages((prev) => prev.filter(msg => !msg.isError));

    switch (lastAction.type) {
      case 'flow1':
        flow1SendRequest(lastAction.payload);
        break;
      case 'flow2':
        sendRequest(lastAction.payload);
        break;
      case 'continue':
        continueSendRequest(lastAction.payload);
        break;
      case 'upload':
        uploadJobDescription(lastAction.payload);
        break;
      default:
        setIsLoading(false);
    }
  };

  const handleStartNewConversation = () => {
    // Clear the current conversation and start fresh
    window.location.href = '/employer/assessment';
  };

  const showErrorMessage = () => {
    const errorMessage: Message = {
      id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
      content: "Sorry, I encountered an error processing your request.",
      sender: 'system',
      timestamp: new Date(),
      isError: true,
      componentType: 'errorWithRetry',
      componentData: {
        onRetry: handleRetry,
        onStartNew: handleStartNewConversation,
      },
    };
    setMessages((prev) => [...prev,errorMessage]);
    setIsLoading(false);
  };

  // Process Flow 1 (CV Comparison) responses
  const processFlow1Response = (data: any) => {
    if (data.assistant_id) {
      setAssistantId(data.assistant_id);
    }
    if (data.current_state) {
      setConversationState(data.current_state);
    }

    const responseData = data.response || {};
    const newMessages: Message[] = [];
    const uiHints = responseData.ui_hints || {};

    // Add assistant message if present
    if (responseData.message) {
      const content = typeof responseData.message === 'string'
        ? responseData.message
        : JSON.stringify(responseData.message);

      // Detect credit error messages
      const creditMatch = content.match(/requires\s+(\d+)\s+credits?.*?(\d+)\s+credits?\s+available/i);
      if (creditMatch) {
        const creditsRequired = parseInt(creditMatch[1], 10);
        const creditsAvailable = parseInt(creditMatch[2], 10);
        setCreditErrorInfo({ creditsRequired, creditsAvailable });
        setShowShortOnCreditModal(true);
        setIsLoading(false);
        return;
      }

      // Check if message already exists for this state
      const messageExists = messages.some(
        msg => msg.state === data.current_state && msg.sender === 'system' && msg.content === content
      );

      const isCustomQuestionPrompt =
        isCustomQuestionStage(data.current_state) &&
        typeof responseData.message === 'string' &&
        responseData.message.includes('What type of custom questions would you like to ask?');

      if (!messageExists && !isCustomQuestionPrompt) {
        newMessages.push({
          id: `${data.current_state}-msg-${data.assistant_id}-${Date.now()}`,
          content,
          sender: 'system',
          timestamp: new Date(),
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // Handle Flow 1 states
    switch (data.current_state) {
      case 'get_experience_level':
        // Check if experience form already exists
        const formExists = messages.some(
          msg => msg.componentType === 'experienceLevelForm' && msg.state === data.current_state
        );

        if (!formExists) {
          // Show experience level form
          newMessages.push({
            id: `${data.current_state}-form-${data.assistant_id}`,
            content: '',
            sender: 'system',
            timestamp: new Date(),
            componentType: 'experienceLevelForm',
            componentData: {
              onSubmit: handleExperienceLevelSubmit,
              isSubmitted: false,
              historicalData: null,
              sections: uiHints.sections, // Pass the sections from API
            },
            state: data.current_state,
            isProcessed: true,
          });
        }
        setShowInput(false);
        setShowOption(false);
        break;

      case 'get_jd_choice':
        // Let ai generate or user upload job description
        setShowOption(false);
        setShowInput(false);
        break;
      case 'get_jd_upload':
        // Let ai generate or user upload job description
        setShowOption(false);
        setShowInput(true);
        break;

      case 'upload_cvs':
        // If CVs were just uploaded, start polling for analysis status
        if (cvJustUploaded && assistantId) {
          // Buffer the flow1 data so we can finish processing after polling completes
          setPendingFlow1Data({ newMessages, responseData, data });
          // Add messages now so the user sees them
          if (newMessages.length > 0) {
            setMessages((prev) => appendMessages(prev, newMessages));
          }
          // Start polling - keep isLoading true to show thinking bubble
          startCvAnalysisPolling(assistantId);
          setCvJustUploaded(false);
          return; // Don't fall through to setIsLoading(false)
        }
        // Hide input when options are present (e.g. "Upload more CVs" / "Done")
        if (responseData.options?.length > 0) {
          setShowInput(false);
        } else {
          setShowInput(true);
        }
        break;

      case 'display_results':
        // Show CV comparison table
        if (uiHints.show_summary && uiHints.summary_data) {
          const comparisonExists = messages.some(
            msg => msg.componentType === 'cvComparison' && msg.state === data.current_state
          );

          if (!comparisonExists) {
            newMessages.push({
              id: `${data.current_state}-comparison-${data.assistant_id}`,
              content: '',
              sender: 'system',
              timestamp: new Date(),
              componentType: 'cvComparison',
              componentData: {
                candidates: uiHints.summary_data,
                disclaimer: "Our recommendations are based solely on the information provided in candidate CVs. Without additional context or behavioral data, these insights should be seen as an initial guide — not a full evaluation of fit.\nFor best results, consider using our structured assessment tools to gain a deeper, evidence-based understanding of each candidate's capabilities.",
                options: responseData.options,
                assistantId: data.assistant_id,
              },
              state: data.current_state,
              isProcessed: true,
            });
          }
        }
        setShowInput(false);
        break;

      default:
        // For other states, show input by default
        setShowInput(true);
        break;
    }

    // Handle options
    if (responseData.options?.length > 0) {
      setCurrentOptions(responseData.options);
      setShowOption(true);
    } else {
      setCurrentOptions(null);
      setShowOption(false);
    }

    // Only append messages if there are new ones
    if (newMessages.length > 0) {
      setMessages((prev) => appendMessages(prev, newMessages));
    }
    // Stop loading
    setIsLoading(false);
  };

  const processApiResponse = (data: any) => {
    if (data.assessment_id) {
      setAssessmentId(data.assessment_id);
    }
    if (data.assistant_id) {
      setAssistantId(data.assistant_id);
    }
    if (data.current_state) {
      setConversationState(data.current_state);
    }
    const responseData = data.response || data.conversation_data || {};
    const newMessages: Message[] = [];
    const earliestComponentTimestamps: { [key: string]: string } = {};

    if (responseData.message) {
      const content = typeof responseData.message === 'string' ? responseData.message : JSON.stringify(responseData.message);

      // Detect credit error messages
      const creditMatch = content.match(/requires\s+(\d+)\s+credits?.*?(\d+)\s+credits?\s+available/i);
      if (creditMatch) {
        const creditsRequired = parseInt(creditMatch[1], 10);
        const creditsAvailable = parseInt(creditMatch[2], 10);
        setCreditErrorInfo({ creditsRequired, creditsAvailable });
        setShowShortOnCreditModal(true);
        setIsLoading(false);
        return;
      }

      const isCustomQuestionPrompt =
        isCustomQuestionStage(data.current_state) &&
        typeof responseData.message === 'string' &&
        responseData.message.includes('What type of custom questions would you like to ask?');

      if (!isCustomQuestionPrompt) {
        const newMessage: Message = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content,
          sender: 'system',
          timestamp: new Date(),
          state: data.current_state,
          isProcessed: true,
        };
        newMessages.push(newMessage);
      }
    }

    const uiHints = responseData.ui_hints || {};
    const composerConfig = getAssistantComposerConfig(uiHints);
    // The talent audit names the link `evaluation_link`; flow_2 uses `application_link`.
    if (uiHints.show_email_preview && (uiHints.application_link || uiHints.evaluation_link) && uiHints.decision_buckets) {
      const componentKey = `jobAssessment-${data.current_state}`;
      if (!earliestComponentTimestamps[componentKey]) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();
        const jobAssessmentData = parseJobAssessmentData(uiHints);
        newMessages.push({
          id: `${Date.now()}-job-assessment`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'jobAssessment',
          componentData: {
            ...jobAssessmentData,
            candidatesToInvite,
          },
          state: data.current_state,
          isProcessed: true,
        });
        setCandidatesToInvite([]);
      }
    }

    // Handle two different structures for expectations/core_responsibilities
    const hasExpectationsInUiHints = uiHints.show_expectations && uiHints.expectations && Object.keys(uiHints.expectations).length > 0;
    const hasCoreResponsibilitiesInMessage = responseData.message && typeof responseData.message === 'object' && responseData.message.core_responsibilities;
    const shouldPoll = responseData.message && typeof responseData.message === 'object' && responseData.message.poll;

    // Only render options form if we're not about to start polling
    if ((hasExpectationsInUiHints || hasCoreResponsibilitiesInMessage) && data.current_state !== 'technical_competence' && !shouldPoll) {
      const componentKey = `optionsForm-${data.current_state}`;
      if (!earliestComponentTimestamps[componentKey]) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();

        let expectations = uiHints.expectations || {};

        // Check if core_responsibilities is in responseData.message instead of ui_hints
        if (hasCoreResponsibilitiesInMessage) {
          // Parse the alternative structure from message.core_responsibilities
          const coreResp = responseData.message.core_responsibilities;
          expectations = {
            core_responsibilities: Object.keys(coreResp)
              .filter(key => key.startsWith('responsibility'))
              .reduce((acc, key, index) => {
                const num = key.replace('responsibility', '');
                const checkKey = `check${num}`;
                acc[`responsibility${index + 1}`] = {
                  title: coreResp[key],
                  description: coreResp[`description${num}`],
                  checked: coreResp[checkKey] !== undefined ? coreResp[checkKey] : true
                };
                return acc;
              }, {} as any)
          };
        }

        newMessages.push({
          id: `${Date.now()}-options-form`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'optionsForm',
          componentData: {
            expectations,
            state: data.current_state,
          },
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // Handle job description preview.
    // NOTE: we intentionally do NOT treat "JD-state without a jd_preview" as an error.
    // create_jd / submit_jd / criteria responses legitimately arrive without a preview
    // while the JD/criteria is still being generated (poll/status messages), or when the
    // state carries criteria data instead of a JD. The previous `else { showErrorMessage() }`
    // here fired a false error in exactly those windows even though the JD generates fine.
    // Genuine request failures are still surfaced by the error/flow1Error/uploadError effect
    // and the API onError toasts. This mirrors useConversationProcessing (history replay),
    // which renders the preview when present and otherwise moves on without erroring.
    if ((data.current_state === 'create_jd' || data.current_state === 'submit_jd' || data.current_state === 'criteria') && uiHints.show_jd_preview && uiHints.jd_preview) {
      const componentKey = `jobDescription-${data.current_state}`;
      if (!earliestComponentTimestamps[componentKey]) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();
        newMessages.push({
          id: `${Date.now()}-job-desc`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'jobDescription',
          componentData: {
            jdPreview: uiHints.jd_preview,
          },
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // The assistant renders the form itself when it answers with a submit option.
    // Keyed on the stage rather than on `technical_competence` so the audit's
    // `review_questions` gets it too.
    if (isCustomQuestionStage(data.current_state)) {
      const componentType = (responseData?.options?.[0]?.value === 'submit_open_ended')
        ? 'addSingleQuestion'
        : (responseData?.options?.[0]?.value === 'submit_multiple_choice')
          ? 'addMultipleQuestions'
          : null;
      const componentKey = `${componentType}-${data.current_state}`;
      if (!earliestComponentTimestamps[componentKey] && componentType) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();
        newMessages.push({
          id: `${Date.now()}-${componentType}`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: componentType,
          componentData: { questions: responseData.questions },
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // flow_2 signals the timeline stage with state `done` + a "Done" option; the
    // talent audit uses state `timeline` with an explicit `show_timeline` hint.
    if ((data.current_state === 'done' && responseData?.options?.[0]?.text === 'Done') || uiHints.show_timeline) {
      const componentKey = 'timeline';
      if (!earliestComponentTimestamps[componentKey]) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();
        newMessages.push({
          id: `${Date.now()}-timeline`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'timeline',
          componentData: {},
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // `review_questions` is the talent audit's equivalent of `technical_competence`.
    if (data.current_state === 'technical_competence' || data.current_state === 'Done' || data.current_state === 'review_questions') {
      if (typeof responseData.message === 'object' && !responseData.message.status) {
        const componentKey = `questions-${data.current_state}`;
        if (!earliestComponentTimestamps[componentKey]) {
          earliestComponentTimestamps[componentKey] = Date.now().toString();
          newMessages.push({
            id: `${Date.now()}-pre-questions`,
            content: isTalentAudit()
              ? `To better understand how employees apply their expertise in day-to-day work, I’ve curated a set of contextual technical questions focused on execution, problem-solving, and operational workflows.`
              : `To validate the technical competence of your candidates, we’ve curated a list of targeted screening questions. These are designed to help candidates articulate how they’ve applied their skills in real-world scenarios.`,
            sender: 'system',
            timestamp: new Date(),
            state: data.current_state,
            isProcessed: true,
          }, {
            id: `${Date.now()}-questions`,
            content: '',
            sender: 'system',
            timestamp: new Date(),
            componentType: 'questions',
            componentData: { questions: responseData.message },
            state: data.current_state,
            isProcessed: true,
          });
        }
      }
    }

    // Server-driven select form (talent audit). The control replaces both the
    // option pills and the composer until it's submitted.
    if (responseData.control) {
      const componentKey = `assistantControl-${data.current_state}`;
      if (!earliestComponentTimestamps[componentKey]) {
        earliestComponentTimestamps[componentKey] = Date.now().toString();
        newMessages.push({
          id: `${Date.now()}-assistant-control`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'assistantControl',
          componentData: { control: responseData.control },
          state: data.current_state,
          isProcessed: true,
        });
      }
    }

    // The audit's assistant never offers the custom-questions stage, so its entry
    // point is added to the options it does send.
    const options = withCustomQuestionEntry(responseData.options, data.current_state, isTalentAudit()) ?? null;

    if (options && options.length > 0) {
      setCurrentOptions(options);
      setShowOption(true);
    } else {
      setCurrentOptions(null);
      setShowOption(false);
    }

    setMessages((prev) => appendMessages(prev, newMessages));
    handleStateTransition({
      state: data.current_state,
      options,
      setShowOption,
      setShowInput,
      setCurrentOptions,
      hasControl: !!responseData.control,
      wantsComposer: composerConfig.wantsComposer,
    });
    setInputHints?.(composerConfig.inputHints);
    setIsLoading(false);

  };

  useEffect(() => {
    if (taskResponse && taskResponse.data.status === 'completed') {
      // Mark this task ID as completed to prevent re-polling
      const completedTaskId = String(taskResponse.data.task_id);
      setCompletedTaskIds(prev => new Set(prev).add(completedTaskId));

      const criteriaData = parseCriteriaData(taskResponse.data.data);
      if (criteriaData) {
        const preMessage: Message = {
          id: `pre-add-questions--criteria-task`,
          content: isTalentAudit()
            ? `Great! Based on your goals, I've prepared suggested evaluation dimensions for this review.\nFeel free to refine any section before proceeding.`
            : `Perfect! I've reviewed the job description.\nHere's a draft of the key assessment criteria I plan to use to evaluate candidates.\nYou're welcome to keep or remove any sections that aren't relevant to screen for at this stage.`,
          sender: 'system',
          timestamp: new Date(),
          state: 'technical_competence',
          isProcessed: false,
        };
        const criteriaMessage: Message = {
          id: `${Date.now()}-criteria-task`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType: 'jobCriteria',
          componentData: { criteriaData },
          state: 'technical_competence',
          isProcessed: false,
        };

        setMessages((prev) => [...prev,preMessage, criteriaMessage]);

        if (taskResponse.data.data.options?.length > 0) {
          setCurrentOptions(taskResponse.data.data.options);
          setShowOption(true);
          setShowInput(false);
        } else {
          setShowOption(false);
          setShowInput(true); // Allow user input if no options
        }
        setConversationState('technical_competence');
        setIsLoading(false);
      } else {
        const errorMessage: Message = {
          id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
          content: 'Error: Invalid criteria data received',
          sender: 'system',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev,errorMessage]);
        setIsLoading(false);
      }
    }
    if (taskError) {
      const errorMessage: Message = {
        id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
        content: `Error: ${taskError}`,
        sender: 'system',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev,errorMessage]);
      setIsLoading(false);
    }
  }, [taskResponse, taskError, setMessages, setCurrentOptions, setShowOption, setShowInput, setConversationState]);

  // Handle CV analysis polling completion (or error fallback)
  useEffect(() => {
    if (!pendingFlow1Data) return;
    if (cvAnalysisStatus === 'completed' || (!isCvAnalysisPolling && cvAnalysisStatus !== 'processing')) {
      const { responseData } = pendingFlow1Data;
      // Show options now that analysis is done; hide input when options are present
      if (responseData.options?.length > 0) {
        setCurrentOptions(responseData.options);
        setShowOption(true);
        setShowInput(false);
      } else {
        setShowInput(true);
      }
      setIsLoading(false);
      setPendingFlow1Data(null);
    }
  }, [cvAnalysisStatus, isCvAnalysisPolling, pendingFlow1Data]);

  useEffect(() => {
    if (response && response !== lastProcessedResponse) {
      processApiResponse(response);
      setLastProcessedResponse(response);
    }
  }, [response, lastProcessedResponse]);

  // `continue` is routed through the continue hook, so its reply lands in
  // `continueResponse`. In flow_2 that reply always kicks off criteria
  // generation, so the polling effect below was enough to drive the chat on.
  // The talent audit answers `continue` with an ordinary message + options
  // (review_role_context -> get_evaluation_scope, review_criteria ->
  // review_questions); without rendering it here the chat sticks on "Thinking".
  //
  // Tracked separately from `lastProcessedResponse`: the two mutations hold
  // independent results, and sharing one marker makes them ping-pong — marking
  // the continue reply leaves the older `response` "unprocessed" again, which
  // re-renders it, which un-marks the continue reply, and so on. That loop is
  // what duplicated the questions card and flickered the composer.
  useEffect(() => {
    if (continueResponse && continueResponse !== lastProcessedContinueResponse) {
      setLastProcessedContinueResponse(continueResponse);
      const shouldPoll = (continueResponse as any)?.response?.message?.poll;
      if (!shouldPoll) {
        processApiResponse(continueResponse);
      }
    }
  }, [continueResponse, lastProcessedContinueResponse]);

  // Handle Flow 1 responses separately with signature gating to avoid reprocessing
  useEffect(() => {
    if (!flow1Response) return;
    try {
      const sigParts = [
        String(flow1Response.assistant_id ?? ''),
        String(flow1Response.current_state ?? ''),
        typeof flow1Response.response?.message === 'string'
          ? flow1Response.response.message
          : JSON.stringify(flow1Response.response?.message ?? ''),
        JSON.stringify(flow1Response.response?.ui_hints ?? {})
      ];
      const signature = sigParts.join('|');
      if (signature !== lastFlow1Signature) {
        // Check if this is a flow1 to flow2 transition (administer_assessment response)
        const responseMessage = flow1Response.response?.message;
        const hasAssessmentId = !!flow1Response.assessment_id;
        const shouldPoll = responseMessage?.poll === true;
        const taskId = responseMessage?.task_id;
        const status = responseMessage?.status;

        if (hasAssessmentId && shouldPoll && taskId && status === 'generating_criteria') {
          // Skip if this task has already been processed
          if (completedTaskIds.has(String(taskId))) {
            setLastFlow1Signature(signature);
            return;
          }

          // Transition from flow1 to flow2
          setAssessmentId(flow1Response.assessment_id);
          localStorage.setItem('hiringAssistantFlowType', 'flow_2');

          // Show progress message
          const progressMessage: Message = {
            id: `${Date.now()}-flow-transition-progress`,
            content: responseMessage.progress_message || 'Generating assessment criteria...',
            sender: 'system',
            timestamp: new Date(),
            state: 'criteria',
            isProcessed: true,
          };
          setMessages((prev) => [...prev,progressMessage]);

          // Start polling for criteria generation
          startPolling(taskId);
          setLastFlow1Signature(signature);
          return;
        }

        processFlow1Response(flow1Response);
        setLastFlow1Signature(signature);
      }
    } catch (_) {
      // Fallback: process once if signature generation fails
      if (flow1Response !== lastProcessedResponse) {
        processFlow1Response(flow1Response);
        setLastProcessedResponse(flow1Response);
      }
    }
  }, [flow1Response, lastFlow1Signature]);


  useEffect(() => {
    // Handle response (typed/pasted job description)
    if (response && response !== lastProcessedResponse) {
      const taskId = response?.response?.message?.task_id;
      const currentState = response?.current_state;
      const shouldPoll = response?.response?.message?.poll;

      // Only process polling if task has not already been completed
      if (!(taskId && completedTaskIds.has(String(taskId)))) {
        if (currentState !== 'technical_competence' && shouldPoll) {
          if (taskId) {
            startPolling(taskId);
            setLastProcessedResponse(response);
          } else {
            const errorMessage: Message = {
              id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
              content: 'Error: Task ID not available for polling (response)',
              sender: 'system',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev,errorMessage]);
            setIsLoading(false);
          }
        }
      }
    }

    // Handle continueResponse
    if (continueResponse && continueResponse !== lastProcessedResponse) {
      const taskId = continueResponse?.response?.message?.task_id;
      const currentState = continueResponse?.current_state;
      const shouldPoll = continueResponse?.response?.message?.poll;

      // Only process polling if task has not already been completed
      if (!(taskId && completedTaskIds.has(String(taskId)))) {
        if (currentState !== 'technical_competence' && shouldPoll) {
          if (taskId) {
            startPolling(taskId);
          } else {
            const errorMessage: Message = {
              id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
              content: 'Error: Task ID not available for polling (continueResponse)',
              sender: 'system',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev,errorMessage]);
            setIsLoading(false);
          }
        }
      }
    }

    // Handle uploadResponse
    if (uploadResponse && uploadResponse !== lastProcessedResponse) {
      const taskId = uploadResponse?.response?.message?.task_id;
      const currentState = uploadResponse?.current_state;
      const shouldPoll = uploadResponse?.response?.message?.poll;

      // Only process polling if task has not already been completed
      if (!(taskId && completedTaskIds.has(String(taskId)))) {
        if (currentState !== 'technical_competence' && shouldPoll) {
          if (taskId) {
            startPolling(taskId);
          } else {
            const errorMessage: Message = {
              id: `${Date.now()}-error-${Math.random().toString(36).substr(2, 9)}`,
              content: 'Error: Task ID not available for polling (uploadResponse)',
              sender: 'system',
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev,errorMessage]);
            setIsLoading(false);
          }
        }
      }
    }
  }, [response, continueResponse, uploadResponse, completedTaskIds]);

  const handleOptionSelect = (option: string) => {
    // Read flowType fresh each time to get the latest value from localStorage
    const flowType = localStorage.getItem("hiringAssistantFlowType");

    // Special handling for manage_candidates - navigate to manage jobs page
    if (option === 'manage_candidates') {
      navigate('/employer/manage-jobs?tab=cv-analysis&cvFilter=all&cvPage=1');
      return;
    }

    // Leaving the custom-questions stage retires any form the recruiter left
    // open, so a later reload can't drag it back onto a stage that's moved on.
    // Matched on the options the stage owns rather than on the one that leaves it:
    // flow_2 leaves via `continue_technical_competence`, but the audit's way
    // forward is whatever its assistant offered and differs per audit type.
    if (!isCustomQuestionOption(option)) {
      clearCustomQuestionDraft();
      customQuestionResumeOptionsRef.current = null;
    }

    // Picking a custom-question type is answered entirely on the client, so the
    // only thing to guard against is opening a second form on top of one the
    // recruiter hasn't finished with. A form that has already been submitted (or
    // was replayed from history) must NOT block a fresh one — that check used to
    // run *after* the transcript and options had been cleared, so a recruiter who
    // added questions once, or simply reloaded, clicked "Open ended" and got a
    // dead screen with no form and no buttons. Ahead of the credit check too, so
    // a double-click can't raise a "short on credits" prompt for a no-op.
    if (customQuestionTypeForOption(option) && hasOpenCustomQuestionForm()) return;

    const selectedOption = currentOptions?.find(opt => opt.value === option);
    const userMessage: Message = {
      id: `${Date.now()}-user-${Math.random().toString(36).substr(2, 9)}`,
      // Welcome cards are titles ("Run Talent Audit") but read as the full
      // description in the transcript — that's the request the assistant answers.
      content: selectedOption?.description || selectedOption?.text || getWelcomeOptionLabel(option) || option,
      sender: 'user',
      timestamp: new Date(),
      // Same reason as the typed path: the step is what tells ChatUser this is a
      // chosen option, not a pasted document.
      state: conversationState,
    };
    if (option === 'scratch' || option === TALENT_AUDIT_OPTION) {
      setAssessmentStatus('initial')
    }

    // Custom questions cost credits — check before any state changes
    if (isCustomQuestionOption(option)) {
      const creditsNeeded = 8;
      if (!ensureCredits(creditsNeeded)) return;
    }

    setMessages((prev) => [...prev,userMessage]);

    // Determine loading phase based on option context
    if (option === 'continue_technical_competence') {
      setLoadingPhase('technical_questions');
    } else if (option === 'continue' || option === 'check_progress') {
      setLoadingPhase('generating_criteria');
    } else {
      setLoadingPhase('general');
    }

    setIsLoading(true);
    setShowOption(false);
    setCurrentOptions(null);

    if (flowType === "flow_1" && option !== 'evaluate') {
      // After flow1→flow2 transition, technical_competence must target the flow2 assessment
      if (option === 'technical_competence' && assessmentId) {
        const payload: any = { input: option, assessment_id: assessmentId };
        setLastAction({ type: 'flow2', payload });
        sendRequest(payload);
      } else {
        const flow1Payload: any = {};
        flow1Payload.input = option;

        if (assistantId) {
          flow1Payload.assistant_id = assistantId;
        }
        setLastAction({ type: 'flow1', payload: flow1Payload });
        flow1SendRequest(flow1Payload);
      }


    } else {

      const payload: any = {
        input: option,
      };

      if (assessmentId) {
        payload.assessment_id = assessmentId;
      }

      if (!assessmentStarted) {
        setAssessmentStarted(true);
      }

      // Opening the stage is answered on the client for the audit, exactly as the
      // type choice below already is for both flows: the audit's assistant has no
      // case for this input, so sending it would strand the recruiter on a reply
      // that doesn't belong to the stage. The options it offered are kept so the
      // recruiter can get back to them once the form is done with.
      if (option === ADD_CUSTOM_QUESTIONS_OPTION && isTalentAudit()) {
        customQuestionResumeOptionsRef.current = (currentOptions ?? [])
          .filter((opt) => !isCustomQuestionOption(opt.value))
          .map(({ text, value }) => ({ text, value }));
        setCurrentOptions(CUSTOM_QUESTION_TYPE_OPTIONS);
        setShowOption(true);
        setShowInput(false);
        setIsLoading(false);
        return;
      }

      const customQuestionType = customQuestionTypeForOption(option);
      if (customQuestionType) {
        const componentType = componentTypeForCustomQuestion(customQuestionType);
        const questionsMessage: Message = {
          id: `${Date.now()}-${componentType}`,
          content: '',
          sender: 'system',
          timestamp: new Date(),
          componentType,
          componentData: {
            questions: null,
          },
          // Filed under the stage's canonical state, whichever state the question
          // was actually asked from, so the live path and history rebuilds agree.
          state: 'technical_competence',
          isProcessed: true,
        };
        // Nothing about this choice reaches the assistant, so remember it locally —
        // it's the only way a reload can put the recruiter back in front of this form.
        openCustomQuestionDraft(assessmentId, customQuestionType, customQuestionResumeOptionsRef.current);
        setIsSubmittedQustions(false);
        // `userMessage` is already in the transcript from the push above; adding it
        // again here rendered the recruiter's choice twice, under a duplicate key.
        setMessages((prev) => [...prev, questionsMessage]);
        setShowOption(true);
        setIsLoading(false);
      } else if (option === 'continue_technical_competence' && currentTechnicalQuestionsRef?.current) {
        // Submit technical competence questions and then proceed with hiring assistant
        submitTechnicalCompetence(currentTechnicalQuestionsRef.current)
        setTimeout(() => {
          setLastAction({ type: 'flow2', payload });
          sendRequest(payload);
        }, 1000);

      } else if (option === 'continue' || option === 'check_progress') {
        setLastAction({ type: 'continue', payload });
        continueSendRequest(payload);
      }
      else {
        if (payload.input !== 'continue') {
          setLastAction({ type: 'flow2', payload });
          sendRequest(payload);
        }
      }
    }
  };

  // Handler for a server-driven `control` form (talent audit). The chosen
  // option label goes into the transcript; the raw value goes on the wire.
  const handleControlSubmit = (input: string, labels: string[]) => {
    const userMessage: Message = {
      id: `${Date.now()}-user-control-${Math.random().toString(36).substr(2, 9)}`,
      content: labels.length > 0 ? labels.join(', ') : input,
      sender: 'user',
      timestamp: new Date(),
      // A multi-select control's joined labels can run long; the step keeps them
      // from being mistaken for a pasted document.
      state: conversationState,
    };
    setMessages((prev) => [...prev, userMessage]);
    setShowOption(false);
    setCurrentOptions(null);
    setShowInput(false);
    setLoadingPhase(getPhaseFromState(conversationState));
    setIsLoading(true);

    const payload: any = { input };
    if (assessmentId) {
      payload.assessment_id = assessmentId;
    }
    setLastAction({ type: 'flow2', payload });
    sendRequest(payload);
  };

  // Handler for Flow 1 experience level form submission
  const handleExperienceLevelSubmit = (formData: any) => {
    const flow1Payload = {
      input: formData,
      assistant_id: assistantId,
    };
    setLastAction({ type: 'flow1', payload: flow1Payload });
    setLoadingPhase('experience_level');
    setIsLoading(true);
    flow1SendRequest(flow1Payload);
    appendAssistantIdToUrlAsQuery(assistantId || '');

  };

  // Handler for Flow 1 CV upload
  const handleCVUpload = (files: File[]) => {
    // Pre-action credit check: 5 credits per CV
    const creditsPerCV = 5;
    const creditsNeeded = files.length * creditsPerCV;
    if (!ensureCredits(creditsNeeded)) return;

    // Convert files to base64 and upload
    const uploadPromises = files.map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          // Remove data URI prefix
          const base64Content = base64.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(uploadPromises).then((base64Files) => {
      const userMessage: Message = {
        id: `${Date.now()}-user-cvs`,
        content: '',
        sender: 'user',
        timestamp: new Date(),
        componentType: 'uploadedCVs',
        componentData: {
          files: files.map(f => ({ name: f.name })),
        },
      };

      setMessages((prev) => [...prev,userMessage]);
      setLoadingPhase('cv_upload');
      setIsLoading(true);
      setCvJustUploaded(true);

      flow1SendRequest({
        user_input: 'upload',
        assistant_id: assistantId,
        files: base64Files.map(content => ({ content })),
      });
    });
  };

  const handleMessageSend = (
    message: string,
    attachments?: File[],
    documentMode = false,
  ) => {
    // Read flowType fresh each time to get the latest value from localStorage
    const flowType = localStorage.getItem("hiringAssistantFlowType");

    const userMessage: Message = {
      id: `${Date.now()}-user-${Math.random().toString(36).substr(2, 9)}`,
      content: message,
      sender: 'user',
      timestamp: new Date(),
      // Carries the step so ChatUser can tell a pasted JD from a long typed answer.
      state: conversationState,
      attachments,
      documentHint: getComposerDocumentHint(documentMode, attachments),
    };

    const documentMessage: Message = {
      id: `${Date.now()}-user-${Math.random().toString(36).substr(2, 9)}`,
      content: `${attachments?.[0]?.name}\n`,
      sender: 'user',
      timestamp: new Date(),
      attachments
    };


    // Credit check for JD upload (pasted/typed JD, no file)
    if ((!attachments || attachments.length === 0) && (flowType === "flow_1") && (conversationState === "upload_jd" || conversationState === "get_jd_upload")) {
      const creditsNeeded = 3;
      if (!ensureCredits(creditsNeeded)) return;
    }

    setLoadingPhase(getPhaseFromState(conversationState));

    if (!attachments) {
      setMessages((prev) => [...prev,userMessage]);
    }

    if (flowType === "flow_1") {
      const flow1Payload: any = {};
      if (message) {
        flow1Payload.input = message;
      }

      if (assistantId) {
        flow1Payload.assistant_id = assistantId;
      }

        if (attachments && attachments.length > 0 && (conversationState === "upload_jd" || conversationState === "get_jd_upload")) {
          // Pre-action credit check: 3 credits required for JD upload
          const creditsNeeded = 3;
          if (!ensureCredits(creditsNeeded)) return;

          const file = attachments[0];
          setMessages((prev) => [...prev,documentMessage]);
          setLoadingPhase('jd_upload');
          setIsLoading(true);

          const reader = new FileReader();
          reader.onload = () => {
            try {
              const base64 = reader.result as string;
              const base64Content = base64.split(",")[1];

              // include filename in payload
              flow1Payload.file = {
                name: file.name || "job_description",
                content: base64Content,
              };
              setLastAction({ type: 'flow1', payload: flow1Payload });
              flow1SendRequest(flow1Payload);
            } catch (err) {
              console.error("Error preparing base64 file for Flow 1 JD upload:", err);
              setIsLoading(false);
              showErrorMessage();
            }
          };
          reader.onerror = (err) => {
            console.error("Error reading file for Flow 1 JD upload:", err);
            setIsLoading(false);
            showErrorMessage();
          };
          reader.readAsDataURL(file);
          return; // prevent fallthrough to immediate request
        }

      if (attachments && attachments.length > 0 && (conversationState === "upload_cvs" || conversationState === "upload_cv")) {
        // Pre-action credit check: 5 credits per CV
        const creditsPerCV = 5;
        const creditsNeeded = attachments.length * creditsPerCV;
        if (!ensureCredits(creditsNeeded)) return;

        // show uploaded CV chips in chat (names)
        const uploadedCVsMessage: Message = {
          id: `${Date.now()}-user-cvs`,
          content: '',
          sender: 'user',
          timestamp: new Date(),
          componentType: 'uploadedCVs',
          componentData: {
            files: attachments.map(f => ({ name: f.name })),
          },
        };
        setMessages((prev) => [...prev,uploadedCVsMessage]);
        setLoadingPhase('cv_upload');
        setIsLoading(true);
        setCvJustUploaded(true);

        // convert all attachments to base64
        const fileToBase64 = (file: File) =>
          new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result as string);
            r.onerror = (e) => reject(e);
            r.readAsDataURL(file);
          });

        Promise.all(attachments.map((f) => fileToBase64(f)))
          .then((base64Results) => {
            const filesPayload = base64Results.map((b64, i) => {
              const content = b64.includes(",") ? b64.split(",")[1] : b64;
              return {
                name: attachments[i].name || `file_${i}`,
                content,
              };
            });

            // build payload for Flow 1 CV upload
            const payload: any = {
              user_input: "upload",
            };
            if (assistantId) payload.assistant_id = assistantId;
            payload.files = filesPayload;
            setLastAction({ type: 'flow1', payload });
            flow1SendRequest(payload);
          })
          .catch((err) => {
            console.error("Error converting CV files to base64:", err);
            setIsLoading(false);
            showErrorMessage();
          });

        return; // prevent fallthrough
      }
      setLastAction({ type: 'flow1', payload: flow1Payload });
      flow1SendRequest(flow1Payload);
    } else {
      // flow_2 and the talent audit share this path — both are keyed by
      // assessment_id and differ only by the flow_type the hook layer adds.
      // Must not be `else if (flowType === "flow_2")`: that silently drops every
      // typed answer in the audit flow.
      if (attachments && attachments.length > 0 && conversationState === "upload_jd") {
        // Pre-action credit check: 3 credits for JD upload
        const creditsNeeded = 3;
        if (!ensureCredits(creditsNeeded)) return;
        try {
          if (assessmentId) {
            setLastAction({ type: 'upload', payload: attachments[0] });
            uploadJobDescription(attachments[0]);
            setMessages((prev) => [...prev,documentMessage]);
            setShowInput(false);
          } else {
            console.error("Missing assessment ID for job description upload");
            setIsLoading(false);
            showErrorMessage();
          }
        } catch (err) {
          console.error("Error uploading job description:", err);
          setIsLoading(false);
          showErrorMessage();
        }
        finally {
          setIsLoading(false);
        }
        return;
      }

      if (attachments && attachments.length > 0) {
        // Nothing was echoed into the transcript on this path, so an upload just
        // vanished from the recruiter's view. Show the same chips the CV upload uses.
        setMessages((prev) => [...prev, {
          id: `${Date.now()}-user-docs-${Math.random().toString(36).substr(2, 9)}`,
          content: '',
          sender: 'user',
          timestamp: new Date(),
          componentType: 'uploadedCVs',
          componentData: { files: attachments.map((f) => ({ name: f.name })) },
        }]);

        if (isTalentAudit()) {
          // The audit takes its role-context document as base64 JSON
          // (`file: { name, content }`) — the same shape flow_1 uses for its JD.
          // multipart/form-data is rejected on this flow, which is what made the
          // upload at get_role_context fail.
          setLoadingPhase(getPhaseFromState(conversationState));
          setIsLoading(true);
          setShowInput(false);

          Promise.all(attachments.map((file) => fileToBase64(file)))
            .then((contents) => {
              const files = contents.map((content, i) => ({
                name: attachments[i].name || `document_${i + 1}`,
                content,
              }));

              const payload: any = { file: files[0] };
              if (assessmentId) payload.assessment_id = assessmentId;
              if (message) payload.input = message;
              // The composer accepts several documents here (a JD plus KPIs), so
              // pass the rest alongside rather than silently dropping them.
              if (files.length > 1) payload.files = files;

              setLastAction({ type: 'continue', payload });
              continueSendRequest(payload);
            })
            .catch((err) => {
              console.error("Error preparing role context documents for upload:", err);
              setIsLoading(false);
              showErrorMessage();
            });
          return;
        }

        // Use FormData when there are file attachments
        const payload: any = new FormData();
        payload.append("input", message);
        if (assessmentId) {
          payload.append("assessment_id", assessmentId);
        }
        // The composer accepts several documents, so send them all under the same
        // key rather than silently dropping everything after the first.
        attachments.forEach((file) => payload.append("file", file));
        setLastAction({ type: 'continue', payload });
        continueSendRequest(payload);
      } else {
        // Use JSON object for text-only messages
        const payload: any = {
          input: message,
        };
        if (assessmentId) {
          payload.assessment_id = assessmentId;
        }
        setLastAction({ type: 'flow2', payload });
        sendRequest(payload);
      }

      setShowInput(false);
    };

    setLoadingPhase(getPhaseFromState(conversationState));
    setIsLoading(true);
  };

  useEffect(() => {
    const anyLoading = loading || flow1Loading || uploadLoading || isPolling || loadingTaskReady || technicalCompetenceLoading || isCvAnalysisPolling;
    setIsLoading(anyLoading);

    // Refine loading phase based on which specific flag is active
    if (anyLoading) {
      if (isCvAnalysisPolling) setLoadingPhase('cv_upload');
      else if (uploadLoading) setLoadingPhase('jd_upload');
      else if (technicalCompetenceLoading) setLoadingPhase('technical_questions');
      else if (loadingTaskReady) setLoadingPhase('task_ready');
      else if (isPolling) setLoadingPhase('generating_criteria');
    }
  }, [loading, flow1Loading, uploadLoading, isPolling, loadingTaskReady, technicalCompetenceLoading, isCvAnalysisPolling]);

  useEffect(() => {
    if (error || flow1Error || uploadError) {
      showErrorMessage();
      setIsLoading(false);
    }
  }, [error, flow1Error, uploadError]);

  return {
    isSubmittedQuestions,
    setIsSubmittedQustions,
    imageUploadScroll,
    setImageUploadScroll,
    handleMessageSend,
    handleOptionSelect,
    sendRequest,
    handleExperienceLevelSubmit,
    handleControlSubmit,
    handleCVUpload,
    handleRetry,
    handleStartNewConversation,
    showShortOnCreditModal,
    setShowShortOnCreditModal,
    showHiringProgressModal,
    setShowHiringProgressModal,
    loadingPhase,
    creditErrorInfo,
    setCreditErrorInfo,
    candidatesToInvite,
    setCandidatesToInvite,
  };
};
